import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import { getApiBaseUrl } from './api';

// Use the same base URL as other API slices
const BASE_URL = getApiBaseUrl();

export const aiApiSlice = createApi({
  reducerPath: 'aiApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: ['AIExtraction', 'LLMCache', 'LLMMetrics'],
  endpoints: (builder) => ({
    // Upload and process Action Request PDF
    uploadActionRequestPDF: builder.mutation({
      query: (pdfFile) => {
        const formData = new FormData();
        formData.append('pdf_file', pdfFile);

        return {
          url: 'ai/upload-ar-pdf/',
          method: 'POST',
          body: formData,
          // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
        };
      },
      transformResponse: (response) => {
        // Transform the response to match what the component expects
        if (response.success && response.ai_processing) {
          return {
            success: true,
            data: response.ai_processing.extracted_data,
            extraction_metadata: {
              confidence_score: response.ai_processing.confidence_score,
              processing_time: response.ai_processing.processing_time,
              model_used: response.ai_processing.model_used,
            },
            file_info: response.file_info,
            pdf_extraction: response.pdf_extraction,
          };
        }
        return response;
      },
      invalidatesTags: ['AIExtraction'],
    }),

    // Test AI connection
    testAIConnection: builder.query({
      query: () => 'ai/test/',
      providesTags: ['AIExtraction'],
    }),

    // Get AI status
    getAIStatus: builder.query({
      query: () => 'ai/status/',
      providesTags: ['AIExtraction'],
    }),

    // Process text with AI (for testing)
    processActionRequestText: builder.mutation({
      query: (document_text) => ({
        url: 'ai/process-action-request/',
        method: 'POST',
        body: { document_text },
      }),
      invalidatesTags: ['AIExtraction'],
    }),

    // Suggest organization structure
    suggestOrganizationStructure: builder.mutation({
      query: (company_data) => ({
        url: 'ai/suggest-organization-structure/',
        method: 'POST',
        body: { company_data },
      }),
    }),

    // Correct and professionalize text using LLM
    correctText: builder.mutation({
      query: (text) => ({
        url: 'llm/correct-text/',
        method: 'POST',
        body: { text },
      }),
      transformResponse: (response) => {
        // Return the corrected text directly for easier consumption
        if (response.success) {
          return {
            success: true,
            correctedText: response.corrected_text,
            originalLength: response.original_length,
            correctedLength: response.corrected_length,
            modelUsed: response.model_used,
          };
        }
        return response;
      },
    }),

    // ========== LLM CACHE & METRICS ==========

    // Get cache performance metrics
    getLLMCacheMetrics: builder.query({
      query: () => 'llm/cache/metrics/',
      providesTags: ['LLMMetrics'],
      transformResponse: (response) => {
        // Transform the nested response structure for easier consumption
        const realTime = response.dashboard?.real_time || {};
        const cachePerformance = response.cache_performance || {};
        const semanticCache = response.semantic_cache || {};

        // Use cache_misses as totalCachedQueries (each unique query started as a cache miss)
        // This gives us the count of unique queries in the database
        const totalCachedQueries = realTime.cache_misses || 0;

        return {
          totalCachedQueries: totalCachedQueries, // Unique queries in cache
          totalCacheHits: realTime.cache_hits || 0, // Total hits on those queries
          cacheMisses: realTime.cache_misses || 0,
          hitRate: realTime.hit_rate || '0%',
          newQueries: 0, // Will be calculated by time period
          apiCallsSaved: realTime.cache_hits || 0, // Each hit saves one API call
          tokensSaved: realTime.tokens_saved || 0,
          speedupFactor: cachePerformance.speedup_factor || '0x',
          avgResponseTime: realTime.avg_response_time_ms || 0,
          avgCacheTime: realTime.avg_cache_response_time_ms || 0,
          semanticHits: realTime.semantic_hits || 0,
          semanticHitRate: realTime.semantic_hit_rate || '0%',
          exactHits: realTime.exact_hits || 0,
          avgSimilarity: realTime.avg_similarity_score || '0',
          compressionRate: realTime.avg_compression_rate || '0%',
          activeRequests: realTime.active_requests || 0,
          errors: realTime.errors || 0,
          errorRate: realTime.error_rate || '0%',
          // Full response for advanced usage
          rawData: response,
        };
      },
    }),

    // Get list of all cached queries
    getLLMCachedQueries: builder.query({
      query: (params = {}) => ({
        url: 'llm/cache/queries/',
        params: {
          page: params.page || 1,
          page_size: params.pageSize || 100,
          period: params.period, // 'week', 'month', 'year'
          ...params,
        },
      }),
      providesTags: ['LLMCache'],
      transformResponse: (response) => {
        // Transform query list for DataGrid
        const queries = response.queries || [];
        return {
          queries: queries.map((q, index) => ({
            id: q.cache_key || index,
            query: q.query || '',
            response: q.response || '',
            hits: q.hit_count || 0,
            apiCallsSaved: q.hit_count || 0, // Each hit saves one API call
            similarity: q.avg_similarity || 0,
            lastUsed: q.last_used || q.created_at,
            topics: q.topics || [],
            primaryTopic: q.primary_topic || 'general',
            cacheKey: q.cache_key,
            createdAt: q.created_at,
            responseLength: q.response_length || 0,
            queryLength: q.query_length || 0,
            model: q.model || '',
            provider: q.provider || '',
            formatType: q.format_type || '',
            numberConstraint: q.number_constraint,
            // User information
            user: q.user
              ? {
                  id: q.user.id,
                  username: q.user.username,
                  firstName: q.user.first_name,
                  lastName: q.user.last_name,
                  email: q.user.email,
                  fullName: q.user.full_name,
                }
              : null,
          })),
          total: response.total || queries.length,
          page: response.page || 1,
          pageSize: response.page_size || 100,
          totalPages: response.total_pages || 1,
        };
      },
    }),

    // Get queries filtered by time period
    getLLMQueriesByPeriod: builder.query({
      query: (period = 'week') => ({
        url: 'llm/cache/queries/by-period/',
        params: { period },
      }),
      providesTags: ['LLMCache'],
    }),

    // Get LLM health status
    getLLMHealth: builder.query({
      query: () => 'llm/health/',
      providesTags: ['LLMMetrics'],
    }),

    // Export cache metrics
    exportLLMMetrics: builder.mutation({
      query: () => ({
        url: 'llm/cache/metrics/export/',
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Clear semantic cache (admin only)
    clearSemanticCache: builder.mutation({
      query: () => ({
        url: 'llm/cache/clear/',
        method: 'POST',
      }),
      invalidatesTags: ['LLMCache', 'LLMMetrics'],
    }),

    // Get hit logs for a specific cache key
    getCacheHitLogs: builder.query({
      query: (cacheKey) => {
        console.log('Getting hit logs for cache key:', cacheKey);
        // The cache key is the full Redis key like "llm:semantic:response:hash"
        return `llm/cache/hit-logs/${cacheKey}/`;
      },
      providesTags: (result, error, cacheKey) => [{ type: 'LLMCache', id: cacheKey }],
    }),

    // ========== LLM CONFIGURATION (Super Admin) ==========

    // Get LLM configuration
    getLLMConfig: builder.query({
      query: () => 'llm/config/',
      providesTags: ['LLMConfig'],
    }),

    // Update LLM configuration
    updateLLMConfig: builder.mutation({
      query: (config) => ({
        url: 'llm/config/update/',
        method: 'POST',
        body: config,
      }),
      invalidatesTags: ['LLMConfig', 'LLMMetrics'],
    }),

    // Test provider connection
    testProviderConnection: builder.mutation({
      query: (providerName) => ({
        url: `llm/config/test/${providerName}/`,
        method: 'GET',
      }),
    }),

    // Get available models for a provider
    getProviderModels: builder.query({
      query: (providerName) => `llm/config/models/${providerName}/`,
    }),

    // Get monthly usage statistics
    getMonthlyUsage: builder.query({
      query: () => 'llm/usage/monthly/',
      providesTags: ['LLMUsage'],
    }),

    // Get top users by LLM usage
    getTopUsers: builder.query({
      query: ({ period = 'month', limit = 10 } = {}) =>
        `llm/usage/top-users/?period=${period}&limit=${limit}`,
      providesTags: ['LLMUsage'],
    }),

    // Get Bureau users by text correction usage
    getBureauUsers: builder.query({
      query: ({ period = 'month', limit = 10 } = {}) =>
        `llm/usage/bureau-users/?period=${period}&limit=${limit}`,
      providesTags: ['LLMUsage'],
    }),

    // Get correction history for a user
    getCorrectionHistory: builder.query({
      query: ({ userId, limit = 20 }) => `llm/usage/correction-history/${userId}/?limit=${limit}`,
      providesTags: (result, error, { userId }) => [{ type: 'LLMUsage', id: userId }],
    }),

    // ========== USER PREFERENCE MANAGEMENT ==========

    // Get all user preferences
    getAllUserPreferences: builder.query({
      query: () => 'llm/preferences/',
      providesTags: ['UserPreferences'],
    }),

    // Update user preference
    updateUserPreference: builder.mutation({
      query: ({ userId, ...data }) => ({
        url: `llm/preferences/${userId}/update/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UserPreferences', 'LLMUsage'],
    }),

    // Reset user preference
    resetUserPreference: builder.mutation({
      query: (userId) => ({
        url: `llm/preferences/${userId}/reset/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserPreferences', 'LLMUsage'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useUploadActionRequestPDFMutation,
  useTestAIConnectionQuery,
  useGetAIStatusQuery,
  useProcessActionRequestTextMutation,
  useSuggestOrganizationStructureMutation,
  useCorrectTextMutation,
  // LLM Cache & Metrics
  useGetLLMCacheMetricsQuery,
  useGetLLMCachedQueriesQuery,
  useGetLLMQueriesByPeriodQuery,
  useGetLLMHealthQuery,
  useExportLLMMetricsMutation,
  useClearSemanticCacheMutation,
  useGetCacheHitLogsQuery,
  // LLM Configuration (Super Admin)
  useGetLLMConfigQuery,
  useUpdateLLMConfigMutation,
  useTestProviderConnectionMutation,
  useGetProviderModelsQuery,
  useGetMonthlyUsageQuery,
  useGetTopUsersQuery,
  useGetBureauUsersQuery,
  useGetCorrectionHistoryQuery,
  // User Preference Management
  useGetAllUserPreferencesQuery,
  useUpdateUserPreferenceMutation,
  useResetUserPreferenceMutation,
} = aiApiSlice;
